import { useEffect, useRef } from 'react';
import type { ApiClient } from './auth/apiClient.js';
import { dispatchSignalWake } from './signalsWake.js';

/**
 * REALTIME — hold the per-user signals SSE stream while the app is signed in AND visible, and turn
 * each server-side wake into the existing {@link dispatchSignalWake} event (every mounted DaySignals
 * refetches the affected day). Sub-second foreground updates; FCM push remains the background/
 * closed-app wake. Visibility-aware: the stream closes when the tab hides (no idle Cloud Run
 * connection hours) and reopens — with an immediate catch-up wake — when it returns. Reconnects with
 * capped backoff when the server cuts the stream (Cloud Run request timeout, redeploys).
 */
export function useSignalStream(api: ApiClient | null, enabled: boolean): void {
  const backoffRef = useRef(1_000);

  useEffect(() => {
    if (!api || !enabled) return;
    let stopped = false;
    let controller: AbortController | null = null;
    let retryTimer: number | null = null;

    const connect = (): void => {
      if (stopped || document.visibilityState === 'hidden') return;
      controller = new AbortController();
      const startedAt = Date.now();
      api
        .signalStream((ev) => {
          backoffRef.current = 1_000; // a delivered wake proves the stream is healthy
          dispatchSignalWake(ev.dateKey ? `journal-${ev.dateKey}` : undefined);
        }, controller.signal)
        .then(() => {
          if (stopped) return;
          // A LONG-lived stream that closed = the server's request timeout — reconnect promptly. A
          // stream that closed IMMEDIATELY (misbehaving proxy, test fake) takes the backoff path so
          // we never hot-loop.
          if (Date.now() - startedAt > 5_000) {
            retryTimer = window.setTimeout(connect, 500);
          } else {
            retryTimer = window.setTimeout(connect, backoffRef.current);
            backoffRef.current = Math.min(backoffRef.current * 2, 60_000);
          }
        })
        .catch(() => {
          if (stopped) return;
          // Startup/stream failure — back off (max 60s) so an outage doesn't hot-loop.
          retryTimer = window.setTimeout(connect, backoffRef.current);
          backoffRef.current = Math.min(backoffRef.current * 2, 60_000);
        });
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        controller?.abort();
      } else {
        // Catch up on anything missed while hidden, then resume the live stream.
        dispatchSignalWake();
        backoffRef.current = 1_000;
        connect();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    connect();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (retryTimer) window.clearTimeout(retryTimer);
      controller?.abort();
    };
  }, [api, enabled]);
}
